import { isStandalone, promptInstall, canInstall, isIOS, showIOSInstallModal, isNativeApp } from '../utils/pwa.js';

const APP_STORE_URL = 'https://apps.apple.com/app/id6760609772';

/**
 * Create pairing form view
 */
export class CreateView {
  /**
   * @param {Object} options
   * @param {Function} options.onSubmit - Callback when form is submitted (nameA, nameB) => void
   */
  constructor({ onSubmit }) {
    this.onSubmit = onSubmit;
    this.inNativeApp = isNativeApp();
    this.element = this.createElement();
    if (!this.inNativeApp) {
      this.initInstallButton();
    }
  }

  /**
   * Create the view element
   * @returns {HTMLElement}
   */
  createElement() {
    const container = document.createElement('div');
    container.className = 'fade-in';

    container.innerHTML = `
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-4">
          <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <h1 class="text-2xl font-bold text-slate-800 mb-2">Handshake</h1>
        <p class="text-slate-600">
          Create a shared verification code with someone you trust.
          <br>
          <span class="text-sm text-slate-500">Like 2FA, but for people.</span>
        </p>
      </div>

      <form id="create-form" class="space-y-4">
        <div>
          <label for="name-a" class="block text-sm font-medium text-slate-700 mb-1">
            Your name
          </label>
          <input
            type="text"
            id="name-a"
            name="nameA"
            required
            autocomplete="given-name"
            placeholder="e.g., Alice"
            class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors text-slate-800 placeholder-slate-400"
          >
        </div>

        <div>
          <label for="name-b" class="block text-sm font-medium text-slate-700 mb-1">
            Their name
          </label>
          <input
            type="text"
            id="name-b"
            name="nameB"
            required
            autocomplete="off"
            placeholder="e.g., Mom"
            class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors text-slate-800 placeholder-slate-400"
          >
        </div>

        <button
          type="submit"
          class="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors focus:ring-2 focus:ring-green-500 focus:ring-offset-2 outline-none"
        >
          Create Verification Code
        </button>
      </form>

      <div class="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-100">
        <h3 class="font-semibold text-slate-800 text-sm mb-2">How it works</h3>
        <ol class="text-sm text-slate-600 space-y-2">
          <li class="flex items-start">
            <span class="flex-shrink-0 w-5 h-5 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center justify-center mr-2 mt-0.5">1</span>
            <span>Create a code and share the link with someone you trust</span>
          </li>
          <li class="flex items-start">
            <span class="flex-shrink-0 w-5 h-5 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center justify-center mr-2 mt-0.5">2</span>
            <span>Both add the code to your authenticator app</span>
          </li>
          <li class="flex items-start">
            <span class="flex-shrink-0 w-5 h-5 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center justify-center mr-2 mt-0.5">3</span>
            <span>When in doubt, ask "What's our code?" to verify it's really them</span>
          </li>
        </ol>
      </div>

      ${!this.inNativeApp ? this.renderWebOnly() : ''}

      <div class="mt-6 text-center">
        <p class="text-xs text-slate-400">
          Your code never leaves your ${this.inNativeApp ? 'device' : 'browser'}.
          <a href="#" class="text-green-600 hover:underline" id="learn-more">Learn more</a>
        </p>
      </div>
    `;

    // Attach form handler
    const form = container.querySelector('#create-form');
    form.addEventListener('submit', (e) => this.handleSubmit(e));

    return container;
  }

  /**
   * Render web-only sections (App Store banner + PWA install)
   * @returns {string}
   */
  renderWebOnly() {
    return `
      <!-- iOS App Banner -->
      <div class="mt-6 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-5 text-white">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h3 class="font-bold text-lg">Get the iPhone app</h3>
            <p class="text-sm text-slate-300">Your code, everywhere you need it</p>
          </div>
        </div>

        <div class="space-y-3 mb-5">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium">Keyboard extension</p>
              <p class="text-xs text-slate-400">Type your code in iMessage, WhatsApp, any app</p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium">Home screen widget</p>
              <p class="text-xs text-slate-400">See your current code at a glance</p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium">Secure by design</p>
              <p class="text-xs text-slate-400">Codes stored in Keychain, never leave your device</p>
            </div>
          </div>
        </div>

        <a href="${APP_STORE_URL}" target="_blank" rel="noopener noreferrer"
          class="flex items-center justify-center gap-2.5 w-full bg-white text-slate-900 font-semibold py-3 px-4 rounded-xl hover:bg-slate-100 transition-colors">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.81-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11"/>
          </svg>
          Download on the App Store
        </a>
      </div>

      <!-- Install Web App Banner -->
      <div id="install-banner" class="mt-6 hidden">
        <button id="install-btn" class="w-full flex items-center justify-center gap-2 py-3 px-4 bg-slate-800 hover:bg-slate-900 text-white font-medium rounded-xl transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Install Handshake
        </button>
        <p class="text-xs text-slate-500 text-center mt-2">
          Access your codes offline, anytime
        </p>
      </div>
    `;
  }

  /**
   * Initialize the install button behavior
   */
  initInstallButton() {
    // Don't show if already installed as PWA
    if (isStandalone()) return;

    const banner = this.element.querySelector('#install-banner');
    const btn = this.element.querySelector('#install-btn');

    if (!banner || !btn) return;

    // Show banner if installation is possible
    if (canInstall()) {
      banner.classList.remove('hidden');
    }

    btn.addEventListener('click', async () => {
      if (isIOS()) {
        showIOSInstallModal();
      } else {
        const installed = await promptInstall();
        if (installed) {
          banner.classList.add('hidden');
        }
      }
    });
  }

  /**
   * Handle form submission
   * @param {Event} e
   */
  handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const nameA = form.nameA.value.trim();
    const nameB = form.nameB.value.trim();

    if (nameA && nameB) {
      this.onSubmit(nameA, nameB);
    }
  }
}
